import { useState, useMemo, useEffect } from "react";
import { DollarSign, AlertTriangle, Info, Search } from "react-feather";
import { PayoutConfirmationModal } from "../../components/affiliate/PayoutConfirmationModal";
import { TaxInformationModal } from "../../components/affiliate/TaxInformationModal";
import { PayoutMethodModal } from "../../components/affiliate/PayoutMethodModal";
import { PaymentMethodData } from "../../components/affiliate/PaymentInfoForm";
import { useAffiliate } from "../../contexts/affiliate";
import { usePortalScope } from "@/contexts/portalScope";
import { formatCurrency } from "../../utils/formatters";
import { payoutService } from "../../services/payout.service";
import { programService, ProgramDetails } from "../../services/program.service";
import {
  paymentMethodsService,
  AffiliatePaymentMethod,
  isPayoutMethodRequiredError,
} from "../../services/paymentMethods.service";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import { AffiliatePayoutStatus } from "../../types/payouts";
import { useProgram } from "@/contexts/program";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function Payout() {
  const { activePrograms:programs } = useProgram();
  const { isMarketplaceHidden } = usePortalScope();
  const { details } = useAffiliate();
  const [activePrograms, setActivePrograms] = useState<ProgramDetails[]>(programs);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBrandId, setSelectedBrandId] = useState<string>("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [programStats, setProgramStats] = useState<{
    available: number;
    requested: number;
    paidOut: number;
    status?: string;
    payoutAvailable?: boolean;
  } | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<AffiliatePaymentMethod[]>([]);
  const [supportedPaymentMethods, setSupportedPaymentMethods] = useState<string[]>([]);
  const [, setIsLoadingPaymentMethods] = useState(false);
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [taxYear, setTaxYear] = useState<number>(new Date().getFullYear());
  const [pendingPayoutRequest, setPendingPayoutRequest] = useState<{ programId: string } | null>(null);
  // Inline add-method form, opened when the server says PAYOUT_METHOD_REQUIRED.
  const [methodModal, setMethodModal] = useState<{
    allowed: string[];
    retry: { programId: string } | null;
  } | null>(null);

  // Tooltip content for each section
  const tooltipContent = {
    available:
      "This shows the total earnings currently ready for payout. You can request this amount immediately, as it has been confirmed and cleared.",
    pending:
      "These are payout requests you've already submitted but haven't been processed yet. Once processed, they'll move into the 'Paid out' category.",
    paidOut:
      "This is the total amount you've received from all past payouts. It serves as a historical record of all completed payments.",
  };

  // Handle tooltip positioning based on screen size and event position
  const handleTooltipShow = (type: string, event: React.MouseEvent) => {
    setActiveTooltip(type);
    
    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    
    // Get position of the current target
    const rect = event.currentTarget.getBoundingClientRect();
    
    // For both mobile and desktop, position relative to the info icon
    // but with different strategies
    if (viewportWidth < 768) { // Mobile
      // Position tooltip above the info icon on mobile
      // Ensure it's not too close to the left edge
      const safeX = Math.max(rect.left + (rect.width / 2), 160); // Ensure at least 160px from left (half tooltip width)
      setTooltipPosition({
        x: safeX,
        y: rect.top - 10 // Position above the icon with a small gap
      });
    } else { // Desktop
      // Position to the right of the info icon
      setTooltipPosition({
        x: rect.right + 8,
        y: rect.top + (rect.height / 2)
      });
    }
  };
  
  // Handle tooltip hide
  const handleTooltipHide = () => {
    setActiveTooltip(null);
    setTooltipPosition(null);
  };



  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        if(activePrograms.length === 0) {
        const p = await programService.findActivePrograms();
        setActivePrograms(p);
        }
        // Set initial selected brand if there are any programs
        if (programs.length > 0) {
          setSelectedBrandId(programs[0].program.sessionId);
        }
      } catch (error) {
        console.error('Error fetching programs:', error);
        setError("Failed to load data. Please try again later.");
        toast.error("Failed to load data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);
  
  // Fetch affiliate payment methods
  const fetchPaymentMethods = async () => {
    if (!details?.id) return;

    try {
      setIsLoadingPaymentMethods(true);
      const methods = await paymentMethodsService.getAffiliatePaymentInfo(details.id);
      setPaymentMethods(methods);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setIsLoadingPaymentMethods(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details?.id]);

  // Fetch program stats when selected brand changes
  useEffect(() => {
    const fetchProgramStats = async () => {
      if (!selectedBrandId) return;

      try {
        setIsLoadingStats(true);
        // Find the program ID for the selected brand
        const selectedProgram = activePrograms.find(
          (p) => p.program.sessionId === selectedBrandId
        );
        if (!selectedProgram) return;

        const stats = await payoutService.getProgramPayoutStatus(
          selectedProgram.program.id
        );
        setProgramStats(stats);
        
        // Fetch supported payment methods for this brand using brandid
        const brandSupportedMethods = await paymentMethodsService.getProgramSupportedPaymentMethods(
          selectedProgram.program.id
        );
        
        // Extract just the names of the payment methods
        const methodNames = brandSupportedMethods.map(method => method.paymentMethod.name);
        setSupportedPaymentMethods(methodNames);
      } catch (error) {
        console.error('Error fetching program data:', error);
        toast.error("Failed to load program statistics");
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchProgramStats();
  }, [selectedBrandId, activePrograms]);

  // Handle click outside to close tooltips
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      // Close tooltips when clicking anywhere else on the page
      if (activeTooltip && !(event.target as Element).closest('.info-tooltip-trigger')) {
        handleTooltipHide();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeTooltip]);

  const selectedProgram = useMemo(() => {
    return activePrograms.find(
      (program) => program.program.sessionId === selectedBrandId
    );
  }, [activePrograms, selectedBrandId]);

  const payoutStatus = useMemo(() => {
    if (!selectedProgram || !programStats) return null;

    const amount = programStats.available;
    let status: AffiliatePayoutStatus = "Available";

    if (amount <= 0) {
      status = "Pending";
    }

    return {
      amount,
      status,
    };
  }, [selectedProgram, programStats]);

  // The affiliate's saved methods this brand can pay with. When the brand has
  // not chosen its methods yet, any saved method counts — the same rule the
  // server applies at request time.
  const matchingMethodNames = useMemo(() => {
    const mine = paymentMethods.map((pm) => pm.paymentMethod.name);
    if (supportedPaymentMethods.length === 0) return mine;
    return mine.filter((name) => supportedPaymentMethods.includes(name));
  }, [paymentMethods, supportedPaymentMethods]);

  const savedMethodsAsFormData = (): PaymentMethodData[] =>
    paymentMethods.map((pm) => ({
      paymentMethodId: pm.paymentMethodId,
      fieldValues: Object.entries(pm.fieldData).map(([fieldName, value]) => ({
        fieldName,
        value,
      })),
    }));

  const refreshAfterRequest = async () => {
    const updatedPrograms = await programService.findActivePrograms();
    setActivePrograms(updatedPrograms);
  };

  /**
   * One submit path for the first attempt and every retry. The two typed
   * server errors each open the form that resolves them, then call back in
   * here with the same program.
   */
  const submitPayoutRequest = async (request: { programId: string }) => {
    try {
      setIsSubmitting(true);
      await payoutService.requestPayout(request);

      toast.success("Payout request submitted successfully!");
      setShowConfirmation(false);
      setPendingPayoutRequest(null);
      await refreshAfterRequest();
    } catch (error: unknown) {
      console.error('Error requesting payout:', error);
      const errorResponse = (
        error as { response?: { data?: { code?: string; message?: string; taxYear?: number } } }
      )?.response?.data;

      if (isPayoutMethodRequiredError(errorResponse)) {
        setShowConfirmation(false);
        setMethodModal({
          allowed: (errorResponse.supportedPaymentMethods ?? []).map((m) => m.name),
          retry: request,
        });
        return;
      }

      if (errorResponse?.code === 'TAX_INFO_REQUIRED') {
        // Store the payout request to retry after tax info is submitted
        setPendingPayoutRequest(request);
        setTaxYear(errorResponse.taxYear || new Date().getFullYear());
        setShowTaxModal(true);
        setShowConfirmation(false);
        return;
      }

      toast.error(errorResponse?.message || "Failed to submit payout request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!selectedProgram || !payoutStatus || payoutStatus.amount <= 0 || payoutStatus.amount < 10) {
      if (payoutStatus && payoutStatus.amount < 10) {
        toast.error("Minimum balance of $10 required to request a payout");
      } else {
        toast.error("No available amount to request");
      }
      return;
    }
    await submitPayoutRequest({ programId: selectedProgram.program.id });
  };

  const handleTaxInfoComplete = async () => {
    setShowTaxModal(false);
    if (pendingPayoutRequest) {
      await submitPayoutRequest(pendingPayoutRequest);
    }
  };

  const handleMethodSaved = async () => {
    const retry = methodModal?.retry ?? null;
    setMethodModal(null);
    await fetchPaymentMethods();
    if (retry) {
      await submitPayoutRequest(retry);
    }
  };

  if (isLoading) {
    return (
      <main>
        <div className="mb-6">
          <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        </div>
        <div className="mb-6">
          <div className="h-9 w-full md:w-64 bg-muted rounded animate-pulse" />
        </div>
        <div className="bg-card rounded-lg shadow-sm border border-border p-6 animate-pulse max-w-md">
          <div className="h-6 w-32 bg-muted rounded mb-4" />
          <div className="h-10 w-48 bg-muted rounded mb-6" />
          <div className="space-y-6">
            <div className="h-4 w-64 bg-muted rounded" />
            <div className="h-4 w-40 bg-muted rounded" />
            <div className="h-4 w-56 bg-muted rounded" />
            <div className="h-4 w-48 bg-muted rounded" />
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Request payout</h1>
        </div>
        <div className="bg-red-50 text-red-700 p-4 rounded-lg border border-red-200">
          {error}
        </div>
      </main>
    );
  }

  if (activePrograms.length === 0) {
    return (
      <main>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Request payout</h1>
        </div>
        <EmptyState
          icon={<Search />}
          title="No active programs"
          body={
            isMarketplaceHidden
              ? "You can request payouts once you're active in a program"
              : "Join brand programs before you can request payouts"
          }
          action={
            isMarketplaceHidden ? undefined : (
              <Button asChild>
                <Link to="/affiliate/brands/search">Find Brands to Join</Link>
              </Button>
            )
          }
        />
      </main>
    );
  }

  return (
    <main>
        {/* Payout method notice: only when the selected brand cannot pay any
            saved method. Adding one happens inline — no trip to Settings. */}
        {selectedProgram && !isLoadingStats && matchingMethodNames.length === 0 && (
          <div className="bg-muted rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="payout-method-notice">
            <AlertTriangle className="text-brand-600 mt-0.5 flex-shrink-0" size={20} />
            <div>
              <p className="text-foreground">
                {supportedPaymentMethods.length > 0
                  ? `${selectedProgram.companyName} pays with ${supportedPaymentMethods.join(", ")}. Add one of these to receive your payout.`
                  : "Add a payout method to receive your payout."}
              </p>
              <button
                type="button"
                onClick={() =>
                  setMethodModal({ allowed: supportedPaymentMethods, retry: null })
                }
                className="text-brand-600 font-medium hover:underline mt-2 inline-block"
              >
                Add payout method
              </button>
            </div>
          </div>
        )}
        
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">Request payout</h1>
        </div>

        {/* Brand Selector — the scoped portal has one brand, already selected
            on load, so the control has nothing to choose between. */}
        <div className={`mb-6${isMarketplaceHidden ? " hidden" : ""}`}>
          <Select value={selectedBrandId} onValueChange={setSelectedBrandId}>
            <SelectTrigger className="w-full md:w-64">
              <SelectValue placeholder="Select brand" />
            </SelectTrigger>
            <SelectContent>
              {activePrograms.map((program) => (
                <SelectItem
                  key={program.id}
                  value={program.program.sessionId}
                >
                  {program.companyName ?? program.id}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats Card */}
        {payoutStatus && programStats && (
          <div className="bg-card rounded-xl shadow-sm overflow-hidden mb-6">
            {/* Balance */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center mb-2">
                <h3 className="text-muted-foreground text-sm font-medium">Balance</h3>
                <div
                  className="ml-1 text-muted-foreground cursor-pointer relative info-tooltip-trigger"
                  onMouseEnter={(e) => handleTooltipShow("available", e)}
                  onMouseLeave={handleTooltipHide}
                  onClick={(e) => handleTooltipShow("available", e)}
                >
                  <Info size={16} />
                  {activeTooltip === "available" && tooltipPosition && (
                    <div 
                      className="fixed z-50 bg-card p-3 rounded-md shadow-lg border border-border w-64 text-sm text-muted-foreground max-w-[90vw]"
                      style={{
                        top: `${tooltipPosition.y}px`,
                        left: `${tooltipPosition.x}px`,
                        transform: window.innerWidth < 768 ? 'translate(-50%, -100%)' : 'translateY(-50%)',
                        maxWidth: '280px'
                      }}
                    >
                      {tooltipContent.available}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-3xl font-bold">
                {isLoadingStats ? (
                  <div className="animate-pulse h-8 w-24 bg-muted rounded"></div>
                ) : (
                  formatCurrency(programStats.available || 0)
                )}
              </div>
            </div>
            
            {/* Pending */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center mb-2">
                <h3 className="text-muted-foreground text-sm font-medium">Pending</h3>
                <div
                  className="ml-1 text-muted-foreground cursor-pointer relative info-tooltip-trigger"
                  onMouseEnter={(e) => handleTooltipShow("pending", e)}
                  onMouseLeave={handleTooltipHide}
                  onClick={(e) => handleTooltipShow("pending", e)}
                >
                  <Info size={16} />
                  {activeTooltip === "pending" && tooltipPosition && (
                    <div 
                      className="fixed z-50 bg-card p-3 rounded-md shadow-lg border border-border w-64 text-sm text-muted-foreground max-w-[90vw]"
                      style={{
                        top: `${tooltipPosition.y}px`,
                        left: `${tooltipPosition.x}px`,
                        transform: window.innerWidth < 768 ? 'translate(-50%, -100%)' : 'translateY(-50%)',
                        maxWidth: '280px'
                      }}
                    >
                      {tooltipContent.pending}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-3xl font-bold">
                {isLoadingStats ? (
                  <div className="animate-pulse h-8 w-24 bg-muted rounded"></div>
                ) : (
                  formatCurrency(programStats.requested || 0)
                )}
              </div>
            </div>
            
            {/* Paid out */}
            <div className="p-6">
              <div className="flex items-center mb-2">
                <h3 className="text-muted-foreground text-sm font-medium">Paid out</h3>
                <div
                  className="ml-1 text-muted-foreground cursor-pointer relative info-tooltip-trigger"
                  onMouseEnter={(e) => handleTooltipShow("paidOut", e)}
                  onMouseLeave={handleTooltipHide}
                  onClick={(e) => handleTooltipShow("paidOut", e)}
                >
                  <Info size={16} />
                  {activeTooltip === "paidOut" && tooltipPosition && (
                    <div 
                      className="fixed z-50 bg-card p-3 rounded-md shadow-lg border border-border w-64 text-sm text-muted-foreground max-w-[90vw]"
                      style={{
                        top: `${tooltipPosition.y}px`,
                        left: `${tooltipPosition.x}px`,
                        transform: window.innerWidth < 768 ? 'translate(-50%, -100%)' : 'translateY(-50%)',
                        maxWidth: '280px'
                      }}
                    >
                      {tooltipContent.paidOut}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-3xl font-bold">
                {isLoadingStats ? (
                  <div className="animate-pulse h-8 w-24 bg-muted rounded"></div>
                ) : (
                  formatCurrency(programStats.paidOut || 0)
                )}
              </div>
            </div>
            
            {/* Supported Payment Methods */}
            <div className="px-6 pb-6">
              <p className="text-sm text-muted-foreground mb-2">Supported payment methods</p>
              <div className="flex flex-wrap gap-2">
                {supportedPaymentMethods.slice(0, 3).map((method, index) => (
                  <span key={index} className="text-sm text-brand-600 bg-brand-50 px-3 py-1 rounded-full">
                    {method}
                  </span>
                ))}
                {supportedPaymentMethods.length > 3 && (
                  <span className="text-sm text-brand-600 bg-brand-50 px-3 py-1 rounded-full">
                    +{supportedPaymentMethods.length - 3}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Request Button */}
        {activePrograms.length > 0 && (
          <div className="flex flex-col items-center md:items-start">
            <Button
              onClick={() => setShowConfirmation(true)}
              className="w-full md:w-auto"
              disabled={
                !payoutStatus ||
                payoutStatus.status !== "Available" ||
                (payoutStatus && payoutStatus.amount < 10) ||
                isSubmitting
              }
            >
              <DollarSign size={18} />
              {isSubmitting ? "Processing..." : "Request payout"}
            </Button>
            
            <Link 
              to="/affiliate/settings?tab=payment" 
              className="text-brand-600 hover:underline text-sm mt-2"
            >
              Edit payment methods
            </Link>
            
            {payoutStatus && payoutStatus.amount > 0 && payoutStatus.amount < 10 && (
              <p className="text-amber-600 text-sm mt-2 flex items-center">
                <AlertTriangle size={14} className="mr-1" />
                Minimum balance of $10 required to request a payout
              </p>
            )}
          </div>
        )}

        {/* Confirmation Modal */}
        {selectedProgram && (
          <PayoutConfirmationModal
            isOpen={showConfirmation}
            onClose={() => setShowConfirmation(false)}
            onConfirm={handleRequestPayout}
            brandName={selectedProgram.companyName}
            amount={payoutStatus?.amount || 0}
            isSubmitting={isSubmitting}
            matchingMethodNames={matchingMethodNames}
          />
        )}

        {/* Inline payout-method form — the one place details are required */}
        <PayoutMethodModal
          isOpen={!!methodModal}
          onClose={() => setMethodModal(null)}
          initialData={savedMethodsAsFormData()}
          allowedMethodNames={methodModal?.allowed ?? []}
          description={
            selectedProgram && (methodModal?.allowed.length ?? 0) > 0
              ? `${selectedProgram.companyName} pays with ${methodModal?.allowed.join(", ")}. Add one of them and your request goes straight through.`
              : undefined
          }
          onSave={(methods) =>
            paymentMethodsService.bulkUpdatePaymentMethod(methods).then(() => undefined)
          }
          onSaved={handleMethodSaved}
          saveLabel={methodModal?.retry ? "Save and request payout" : "Save payout method"}
        />

        {/* Tax Information Modal */}
        <TaxInformationModal
          isOpen={showTaxModal}
          taxYear={taxYear}
          onComplete={handleTaxInfoComplete}
        />
    </main>
  );
}